import { Injectable } from '@angular/core';
import type { User } from '../models';
import { captureException, withSentry } from './sentry-client';

/**
 * SentryService — Sentry error tracking: user context + handled-error reporting.
 *
 * Call setUser() after a successful login so every subsequent error
 * is tied to a specific merchant. Call clearUser() on logout to
 * prevent one session's context bleeding into the next.
 *
 * Handled errors go through captureException() here rather than importing
 * `* as Sentry` at each call site, so consumers stay unit-testable — see the
 * note on that method.
 *
 * The SDK itself is loaded lazily (see `sentry-client.ts`), so calls made before
 * it lands are buffered rather than lost.
 */
@Injectable({
  providedIn: 'root',
})
export class SentryService {
  /**
   * Attach merchant identity to all Sentry events for this session.
   * This turns anonymous errors into actionable merchant issues.
   */
  setUser(user: User, merchantId: string): void {
    withSentry((sentry) => {
      sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.merchantName,
      });
      sentry.setTag('merchant_id', merchantId);
      sentry.setTag('merchant_name', user.merchantName);
      sentry.setTag('user_role', user.role);
    });
  }

  /**
   * Report a handled error (one we caught and recovered from) to Sentry.
   *
   * Wrapped here rather than importing `* as Sentry` at each call site so consumers can
   * be unit-tested with a plain DI fake. Mocking the `@sentry/angular` ES module directly
   * is NOT deterministic: Vite pre-bundles it into `vite/deps/@sentry_angular.js`, so a
   * `vi.mock('@sentry/angular')` registered against the bare specifier may not intercept
   * the optimized-dep module the code under test actually imports — depending on optimizer
   * cache state and the shape of the module graph. That produced an intermittently failing
   * config.service spec.
   */
  captureException(error: unknown, context?: Parameters<typeof captureException>[1]): void {
    captureException(error, context);
  }

  /**
   * Clear user context on logout to prevent session data leaking
   * into the next session on the same browser.
   */
  clearUser(): void {
    withSentry((sentry) => {
      sentry.setUser(null);
      sentry.setTag('merchant_id', null);
      sentry.setTag('merchant_name', null);
      sentry.setTag('user_role', null);
    });
  }
}
